import { formatDate as formatDisplayDate, formatShortDate } from '@/lib/formatDate';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, FileText, Receipt, Printer,
  ChevronDown, ChevronUp, Users, IndianRupee, Calendar, Pencil,
  AlertCircle, RefreshCw, Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getPatient, getPatientPrescriptions, getPatientInvoices,
  getDoctors, queueWalkin, recordInvoicePayment,
  getBillingServices, createBillingInvoice, getPatientDeposits,
} from '@/lib/api';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';
import PrintDepositReceiptModal, { type DepositReceipt } from '@/components/Billing/PrintDepositReceiptModal';
import { RefundDialog } from '@/components/IPD/IPDChargesTab';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import { usePageTitle } from "@/hooks/usePageTitle";
import { PatientBanner } from "@/components/Patient/PatientBanner";
import { StatusChip } from "@/components/ui/status-chip";
import { invoiceStatus } from "@/components/Billing/InvoiceTable";
import { cn } from "@/lib/utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  return formatDisplayDate(dateStr);
}

function formatDateShort(dateStr: string) {
  return formatShortDate(dateStr);
}

function formatYear(dateStr: string) {
  if (!dateStr) return '';
  return new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00')).getFullYear().toString();
}

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
const MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card', cheque: 'cheque',
  netbanking: 'netbanking', 'bank transfer': 'netbanking',
  insurance: 'other', other: 'other', advance: 'advance',
};

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

// ─── Prescription card ────────────────────────────────────────────────────────

function PrescriptionGroup({ prescriptions, onPrint }: { prescriptions: any[]; onPrint: (rx: any) => void }) {
  const [expanded, setExpanded] = useState(false);
  const primary = prescriptions[0];
  const multiple = prescriptions.length > 1;

  return (
    <li>
      <div className="flex items-center gap-3 min-h-10 pl-4 pr-2 py-1">
        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 flex items-baseline gap-x-2">
          <span className="text-sm font-medium text-foreground whitespace-nowrap">
            Prescription{multiple && <span className="ml-1 font-mono text-xs text-muted-foreground tabular-nums">×{prescriptions.length}</span>}
          </span>
          <span className="text-sm text-muted-foreground truncate">
            {primary.doctor_name ? drName(primary.doctor_name) : 'Doctor'}
            {primary.diagnosis && <> · <span className="text-foreground/80">{primary.diagnosis}</span></>}
          </span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {multiple && (
            <Button
              variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Hide the other prescriptions' : `Show all ${prescriptions.length} prescriptions`}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
          <Button
            variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            onClick={() => onPrint(primary)}
            title="Print prescription" aria-label="Print prescription"
          >
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Every prescription written that day */}
      {multiple && expanded && (
        <ul className="ml-11 mr-2 mb-2 border-l border-border">
          {prescriptions.map((rx, i) => (
            <li key={rx.id} className="flex items-center gap-3 min-h-9 pl-3">
              <span className="text-sm text-foreground whitespace-nowrap">Prescription {i + 1}</span>
              <span className="text-sm text-muted-foreground truncate flex-1">
                {rx.diagnosis || (rx.medicines?.length ? `${rx.medicines.length} medicine${rx.medicines.length > 1 ? 's' : ''}` : 'No details')}
              </span>
              <Button
                variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
                onClick={() => onPrint(rx)}
                aria-label={`Print prescription ${i + 1}`}
              >
                <Printer className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FrontDeskPatientPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();

  const [patient, setPatient] = useState<any>(null);
  usePageTitle(patient?.name);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);

  // Modals
  const [collectInvoice, setCollectInvoice] = useState<any>(null);
  const [printRx, setPrintRx] = useState<any>(null);
  const [printInvoice, setPrintInvoice] = useState<any>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingServices, setBillingServices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // Amount due — the patient's ledger balance, the same figure as the Billing tab and the patient page
  const [outstanding, setOutstanding] = useState(0);
  // Advance held (deposits not yet used) — a chip in the banner, so the desk knows to use it on a bill
  const [advance, setAdvance] = useState(0);
  // A bill taken with an advance: the receipt for the advance, shown once the bill is saved
  const [depositReceipt, setDepositReceipt] = useState<DepositReceipt | null>(null);
  // Refund follows role access (billing:refund); roles without a clinical profile, like admin by default, can't open the doctor's patient page — so it lives here too
  const [showRefund, setShowRefund] = useState(false);
  const refreshBalance = useCallback(() => {
    if (!patientId || !currentUser?.clinic_id || !can('billing:read')) return;
    getPatientDeposits(patientId, currentUser.clinic_id)
      .then(d => { setOutstanding(Number(d?.outstanding || 0)); setAdvance(Number(d?.available_advance || 0)); })
      .catch(() => { /* keep the last figure */ });
  }, [patientId, currentUser?.clinic_id, can]);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const [patientData, rxData, invData, docData] = await Promise.all([
        getPatient(patientId),
        can('prescriptions:read') ? getPatientPrescriptions(patientId) : Promise.resolve([]),
        can('billing:read')       ? getPatientInvoices(patientId)       : Promise.resolve([]),
        getDoctors(),
      ]);
      if (!patientData) { setError('Patient not found'); return; }
      setPatient(patientData);
      setPrescriptions(Array.isArray(rxData) ? rxData : []);
      setInvoices(Array.isArray(invData) ? invData : []);
      const clinicDocs = (Array.isArray(docData) ? docData : []).filter(
        (d: any) => d.clinic_id === currentUser?.clinic_id
      );
      if (clinicDocs.length) {
        setDoctors(clinicDocs);
        // The patient's own doctor; the first one only when they have none here
        const own = clinicDocs.find((d: any) => d.id === patientData.doctor_id);
        setSelectedDoctorId(prev => prev || (own ?? clinicDocs[0]).id);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load patient';
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [patientId, can]);

  useEffect(() => {
    load();
    refreshBalance();
  }, [load, refreshBalance]);

  const handleAddToQueue = async () => {
    if (!selectedDoctorId) { toast({ title: 'Select a doctor first', variant: 'destructive' }); return; }
    setQueueLoading(true);
    try {
      await queueWalkin(patientId!, selectedDoctorId, currentUser!.clinic_id!, false);
      toast({ title: 'Added to queue', description: `${patient.name} added to queue` });
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    } finally {
      setQueueLoading(false);
    }
  };

  const handleNewInvoice = async () => {
    setBillingLoading(true);
    try {
      const svcs = await getBillingServices(currentUser!.clinic_id!, currentUser?.doctor_id);
      setBillingServices(Array.isArray(svcs) ? svcs : (svcs as any)?.services ?? []);
    } catch {
      setBillingServices([]);
    } finally {
      setBillingLoading(false);
    }
    setShowBillingModal(true);
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      const created = await createBillingInvoice({
        patient_id:      patientId!,
        doctor_id:       invoiceData.doctorId || selectedDoctorId || currentUser?.doctor_id || '',
        clinic_id:       currentUser!.clinic_id!,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items,
        discount_amount: invoiceData.discount    || 0,
        discount_type:   invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        discount_reason: invoiceData.discountReason,
        tax_amount:      invoiceData.taxAmount   || 0,
        notes:           invoiceData.notes       || undefined,
        payments:                     invoiceData.payments    || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount      || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode || undefined,
      });
      // An advance taken with the bill: its receipt is one click away in the message
      const receipt: DepositReceipt | null = invoiceData.depositAmount ? {
        patient_id: patientId!, clinic_id: currentUser!.clinic_id!, doctor_id: invoiceData.doctorId || selectedDoctorId || undefined,
        amount: invoiceData.depositAmount, payment_mode: invoiceData.depositPaymentMode,
        receipt_number: created?.deposit_receipt_number,
      } : null;
      toast({ title: 'Invoice created', description: `Bill created for ${patient.name}${receipt ? ` · ₹${receipt.amount.toLocaleString('en-IN')} kept as advance` : ''}`,
              duration: receipt ? 10000 : undefined, // long enough to reach the button
              action: receipt ? <ToastAction altText="Print advance receipt" onClick={() => setDepositReceipt(receipt)}>Print advance receipt</ToastAction> : undefined });
      setShowBillingModal(false);
      const invData = await getPatientInvoices(patientId!);
      setInvoices(Array.isArray(invData) ? invData : []);
      refreshBalance();
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    }
  };

  const handleCollectPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    if (!collectInvoice) return;
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_mode:     (MODE_MAP[method?.toLowerCase()] ?? 'cash') as PaymentMode,
        payment_date:     new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app:          meta?.upi_app          || undefined,
        bank_name:        meta?.bank_name         || undefined,
      });
      toast({ title: 'Payment recorded', description: `₹${amount} collected` });
      setCollectInvoice(null);
      const invData = await getPatientInvoices(patientId!);
      setInvoices(Array.isArray(invData) ? invData : []);
      refreshBalance();
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    }
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const totalBilled = invoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);

  const groupedVisits = React.useMemo(() => {
    const map: Record<string, { date: string; prescriptions: any[]; invoices: any[] }> = {};
    prescriptions.forEach(rx => {
      const date = (rx.date || rx.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].prescriptions.push(rx);
    });
    invoices.forEach(inv => {
      const date = (inv.invoice_date || inv.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].invoices.push(inv);
    });
    return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
  }, [prescriptions, invoices]);

  const firstVisitDate = groupedVisits.length > 0
    ? formatDate(groupedVisits[groupedVisits.length - 1].date)
    : null;

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!loading && !can('patients:read')) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <button
          onClick={() => navigate('/frontdesk')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Front Desk
        </button>
        <div className="rounded-2xl border border-border bg-muted/30 p-12 text-center">
          <p className="text-sm text-muted-foreground">You don't have permission to view patient details.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-4" aria-busy="true" aria-label="Loading patient">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-4 w-40 rounded bg-muted animate-pulse" />
            <div className="h-3 w-56 rounded bg-muted/70 animate-pulse" />
          </div>
        </div>
        <div className="h-16 rounded-2xl bg-muted/60 animate-pulse mb-6" />
        {[0, 1, 2].map(i => <div key={i} className="h-14 rounded-xl bg-muted/50 animate-pulse mb-2" />)}
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <button
          onClick={() => navigate('/frontdesk')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Front Desk
        </button>
        <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-12 text-center">
          <AlertCircle className="w-8 h-8 text-destructive/60 mx-auto mb-3" />
          <p className="text-sm font-medium text-destructive mb-4">{error || 'Patient not found'}</p>
          <Button variant="outline" size="sm" onClick={load} className="gap-2">
            <RefreshCw className="w-3.5 h-3.5" /> Try Again
          </Button>
        </div>
      </div>
    );
  }


  const figures = [
    { label: 'Visits',      value: String(groupedVisits.length) },
    { label: 'First visit', value: firstVisitDate ?? '—', date: true },
    { label: 'Billed',      value: `₹${totalBilled.toLocaleString('en-IN')}` },
    { label: 'Outstanding', value: `₹${outstanding.toLocaleString('en-IN')}`, due: outstanding > 0 },
  ];

  return (
    <div>

      {/* ── Patient header — pinned from md; on phones it would hold a third of the screen ── */}
      <div
        className="md:sticky z-10 bg-card border-b border-border"
        style={{ top: 'var(--topbar-height, 60px)' }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Patient banner — identity and safety chips, same on every patient screen */}
          <PatientBanner
            patient={patient}
            outstanding={outstanding}
            advance={advance}
            leading={
              <Button
                variant="ghost" size="sm" className="h-8 w-8 p-0 -ml-2 flex-shrink-0"
                onClick={() => navigate('/frontdesk')}
                aria-label="Back to Front Desk" title="Back to Front Desk"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            }
            actions={can('patients:write') ? (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowEditModal(true)}>
                <Pencil className="w-4 h-4" /> Edit
              </Button>
            ) : undefined}
          />

          {/* Actions row — Add to queue is the one solid action */}
          {(can('queue:write') || can('billing:write') || can('appointments:write') || (can('billing:refund') && advance > 0)) && (
            <div className="flex items-center gap-2 px-4 sm:px-6 pb-3 flex-wrap">
              {doctors.length > 1 && (can('queue:write') || can('billing:write')) && (
                <Select value={selectedDoctorId} onValueChange={setSelectedDoctorId}>
                  <SelectTrigger className="h-8 touch:h-10 w-44 text-sm" aria-label="Doctor">
                    <SelectValue placeholder="Doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map(d => (
                      <SelectItem key={d.id} value={d.id}>{drName(d.name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {can('queue:write') && (
                <Button size="sm" className="gap-1.5" onClick={handleAddToQueue} disabled={queueLoading}>
                  {queueLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  {queueLoading ? 'Adding…' : 'Add to queue'}
                </Button>
              )}
              {can('billing:write') && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={handleNewInvoice}>
                  <IndianRupee className="w-4 h-4" /> New invoice
                </Button>
              )}
              {can('appointments:write') && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowBookModal(true)}>
                  <Calendar className="w-4 h-4" /> Book appointment
                </Button>
              )}
              {can('billing:refund') && advance > 0 && (
                <Button size="sm" variant="outline" onClick={() => setShowRefund(true)}>
                  Refund ₹{advance.toLocaleString('en-IN')} advance
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 pb-10 space-y-6">

        {/* ── At a glance — one row of figures; grid lines are the 1px gap over the border colour ── */}
        {(groupedVisits.length > 0 || totalBilled > 0) && (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border rounded-lg border border-border overflow-hidden">
            {figures.map(f => (
              <div key={f.label} className="bg-card px-4 py-3 min-w-0">
                <dt className="text-xs text-muted-foreground">{f.label}</dt>
                <dd className="mt-0.5 truncate">
                  <span className={cn('text-lg font-semibold tabular-nums', !f.date && 'font-mono', f.due ? 'rounded bg-highlight px-1.5 text-highlight-foreground' : 'text-foreground')}>
                    {f.value}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        )}

        {/* ── Visit history: one list, a date heading per visit ─────────────── */}
        <section aria-labelledby="visits-heading">
          <h2 id="visits-heading" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Visits</h2>

          {groupedVisits.length === 0 ? (
            <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
              <FileText className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-semibold text-foreground">No visits yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add to queue or create an invoice to begin recording visits.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              {groupedVisits.map((visit, i) => {
                const sameYear = new Date().getFullYear().toString() === formatYear(visit.date);
                const showRx = can('prescriptions:read') && visit.prescriptions.length > 0;
                const shownInvoices = can('billing:read') ? visit.invoices : [];
                if (!showRx && shownInvoices.length === 0) return null;

                return (
                  <section key={visit.date} aria-label={formatDate(visit.date)} className={cn(i > 0 && 'border-t border-border')}>
                    <div className="flex items-center gap-2 h-8 px-4 bg-muted/40 border-b border-border">
                      <h3 className="text-2xs font-medium uppercase tracking-wide text-muted-foreground tabular-nums">
                        {sameYear ? formatDateShort(visit.date) : formatDate(visit.date)}
                      </h3>
                      {i === 0 && <StatusChip tone="accent">Latest</StatusChip>}
                    </div>

                    <ul className="divide-y divide-divider">
                      {showRx && <PrescriptionGroup prescriptions={visit.prescriptions} onPrint={setPrintRx} />}

                      {shownInvoices.map(inv => {
                        const due = inv.payment_status === 'unpaid' || inv.payment_status === 'partial';
                        const chip = invoiceStatus(inv);
                        return (
                          <li key={inv.id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 min-h-10 pl-4 pr-2 py-1">
                            <Receipt className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
                            <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span className="text-sm font-medium text-foreground whitespace-nowrap">Invoice</span>
                              {inv.invoice_number && (
                                <span className="font-mono text-xs text-muted-foreground truncate">{inv.invoice_number}</span>
                              )}
                              <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                            </div>
                            <span className="font-mono text-sm tabular-nums text-foreground whitespace-nowrap text-right">
                              ₹{Number(inv.total_amount).toLocaleString('en-IN')}
                            </span>

                            {/* Fixed width from sm, so amounts line up whether or not Collect is shown; on phones Collect takes its own line */}
                            <div className={cn('flex items-center justify-end gap-0.5 flex-shrink-0 sm:w-[8.75rem] sm:touch:w-[10rem]', can('billing:write') && due && 'max-sm:w-full')}>
                              {can('billing:write') && due && (
                                <Button variant="outline" size="sm" className="mr-1" onClick={() => setCollectInvoice(inv)}>
                                  Collect
                                </Button>
                              )}
                              <Button
                                variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                onClick={() => setPrintInvoice(inv)}
                                title="Print invoice" aria-label="Print invoice"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                  const amount = Number(inv.total_amount).toLocaleString('en-IN');
                                  const status = inv.payment_status === 'paid' ? 'fully paid ✓' : `₹${Number(inv.balance_due).toLocaleString('en-IN')} due`;
                                  const msg = encodeURIComponent(`Dear ${patient.name},\n\nYour bill (${inv.invoice_number}) for ₹${amount} is ${status}.\n\nThank you.`);
                                  const phone = patient.phone?.replace(/\D/g, '');
                                  window.open(phone ? `https://wa.me/91${phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
                                }}
                                title="Share on WhatsApp" aria-label="Share on WhatsApp"
                              >
                                <Share2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}

      {printRx && (
        <PrintPrescriptionPreviewModal open={!!printRx} prescription={printRx} onClose={() => setPrintRx(null)} />
      )}
      {printInvoice && (
        <PrintBillPreviewModal open={!!printInvoice} invoice={printInvoice} onClose={() => setPrintInvoice(null)} />
      )}

      <PrintDepositReceiptModal open={!!depositReceipt} deposit={depositReceipt} onClose={() => setDepositReceipt(null)} />
      {currentUser?.clinic_id && (
        <RefundDialog patientId={patientId} clinicId={currentUser.clinic_id} doctorId={selectedDoctorId || undefined} spare={advance}
          open={showRefund} onClose={() => setShowRefund(false)} onDone={refreshBalance} />
      )}

      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollectPayment}
        loading={false}
      />

      {showBillingModal && (
        <MedicalBillingModal
          isOpen
          onClose={() => setShowBillingModal(false)}
          patient={patient}
          services={billingServices}
          onCreateInvoice={handleCreateInvoice}
          loading={billingLoading}
          clinicId={currentUser!.clinic_id!}
          doctorId={selectedDoctorId || currentUser?.doctor_id || ''}
          doctors={doctors}
        />
      )}

      {showBookModal && (
        <AddAppointmentModal
          isOpen
          onClose={() => setShowBookModal(false)}
          patientId={patientId}
          patients={patient ? [patient] : []}
          showPatientSelection={false}
          onAdded={() => setShowBookModal(false)}
        />
      )}

      {showEditModal && (
        <PatientFormModal
          isOpen
          onClose={() => setShowEditModal(false)}
          onSuccess={async () => {
            setShowEditModal(false);
            const updated = await getPatient(patientId!);
            if (updated) setPatient(updated);
          }}
          mode="edit"
          patientId={patient?.id}
          initialData={{
            name: patient.name ?? '', age: patient.age?.toString() ?? '',
            gender: patient.gender ?? '', phone: patient.phone ?? '',
            phone_2: patient.phone_2 ?? '', email: patient.email ?? '',
            address: patient.address ?? '', city: patient.city ?? '',
            pin_code: patient.pin_code ?? '', blood_group: patient.blood_group ?? '',
            allergies: patient.allergies ?? '', date_of_birth: patient.date_of_birth ?? '',
            chronic_conditions: patient.chronic_conditions ?? '',
            current_medications: patient.current_medications ?? '',
            preferred_language: patient.preferred_language ?? 'English',
            aadhar_number: patient.aadhar_number ?? '',
            emergency_contact_name: patient.emergency_contact_name ?? '',
            emergency_contact_phone: patient.emergency_contact_phone ?? '',
            payment_preference: patient.payment_preference ?? 'Cash',
          }}
          showPrescriptionOption={false}
          showQueueOption={false}
          title="Edit Patient"
        />
      )}
    </div>
  );
}
